import React, { useState, useRef } from 'react';
import { Image, Video, Send, X } from 'lucide-react';

interface MessageInputProps {
  onSend: (content: string, mediaFile?: File) => Promise<void>;
}

export function MessageInput({ onSend }: MessageInputProps) {
  const [content, setContent] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const validVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
      const validTypes = [...validImageTypes, ...validVideoTypes];

      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image or video file');
        return;
      }

      // Validate file size (10MB for images, 100MB for videos)
      const maxSize = file.type.startsWith('video/') ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`File size must be less than ${file.type.startsWith('video/') ? '100MB' : '10MB'}`);
        return;
      }

      setMediaFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!content.trim() && !mediaFile) || sending) return;

    setSending(true);
    try {
      await onSend(content, mediaFile || undefined);
      setContent('');
      setMediaFile(null);
      setMediaPreview(null);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 border-t dark:border-dark-border bg-white dark:bg-dark-paper">
      {mediaPreview && (
        <div className="mb-4 relative group">
          {mediaFile?.type.startsWith('video/') ? (
            <video
              src={mediaPreview}
              controls
              className="max-h-48 rounded-lg"
            />
          ) : (
            <img
              src={mediaPreview}
              alt="Preview"
              className="max-h-48 rounded-lg"
            />
          )}
          <button
            type="button"
            onClick={() => {
              setMediaFile(null);
              setMediaPreview(null);
            }}
            className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Type a message..."
            className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 dark:border-dark-border focus:ring-2 focus:ring-royal-purple focus:border-transparent resize-none overflow-hidden bg-white dark:bg-dark-elevated text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            style={{
              height: content ? 'auto' : '48px',
              minHeight: '48px',
              maxHeight: '200px'
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleMediaChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-royal-purple dark:hover:text-royal-purple hover:bg-gray-100 dark:hover:bg-dark-paper transition-colors"
            >
              <Image className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.accept = 'video/*';
                  fileInputRef.current.click();
                  fileInputRef.current.accept = 'image/*,video/*';
                }
              }}
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-royal-purple dark:hover:text-royal-purple hover:bg-gray-100 dark:hover:bg-dark-paper transition-colors"
            >
              <Video className="w-5 h-5" />
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={(!content.trim() && !mediaFile) || sending}
          className="p-3 rounded-xl bg-royal-purple text-white hover:bg-royal-purple/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </form>
  );
}